import { useState, useLayoutEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Dialog,
    Box,
    Typography,
    IconButton,
    TextField,
    InputAdornment,
    Button,
    Chip,
    Divider,
    Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import LockIcon from '@mui/icons-material/Lock';
import { selectLicense, selectPlanDefinitions } from '../../store/actions/licenseActions';
import { isWidgetAllowed } from '../../utils/licenseUtils';
import { TemplatePreview, RealWidgetPreview } from '../WidgetPreview';
import { heritageColors } from '../../heritageTheme.ts';
import { WIZARD_CONFIG } from '../WidgetSetupWizard/wizardStepsConfig';
import { TEMPLATE_META } from '../TemplateSelector/templateMeta';
import { PARNAS_TEMPLATES } from '../../business/ParnasWidget/templates/index';

// ─── Categories ──────────────────────────────────────────────────
const WIDGET_CATEGORIES = {
    'זמנים':  ['ZmanimWidget', 'ShabbatWidget', 'MoadWidget', 'TefilotWidget', 'OmerWidget', 'InyaneiHaYomWidget', 'LimudimWidget', 'HalachicTimesWidget'],
    'קהילה':  ['ContactsWidget', 'SimchaWidget', 'EmergencyWidget', 'ParnasWidget', 'SeatingMapWidget'],
    'תוכן':   ['NoticeBoardWidget', 'TextWidget', 'ImageWidget'],
    'כספים':  ['ExpensesAnalyticsWidget', 'FinancialGaugeWidget'],
    'אחר':    ['WeatherWidget', 'ClockWidget'],
};

// Widgets hidden from catalog (still appear in my-widgets / saved)
const HIDDEN_FROM_CATALOG = ['NitzavSpotlightWidget'];

// Returns { label, desc, bestFor } for a given template, or null
const getTemplateMeta = (componentKey, templateName) => {
    if (componentKey === 'ParnasWidget') {
        const pt = PARNAS_TEMPLATES.find(t => t.id === templateName);
        if (pt) return { label: pt.label, desc: pt.description, bestFor: null };
    }
    return TEMPLATE_META[componentKey]?.[templateName] ?? null;
};

const getWidgetCategory = (componentKey) => {
    for (const [cat, keys] of Object.entries(WIDGET_CATEGORIES)) {
        if (keys.includes(componentKey)) return cat;
    }
    return 'אחר';
};

const getWidgetMeta = (componentKey) => WIZARD_CONFIG[componentKey]?.intro ?? null;

// ─── WidgetGalleryDialog ─────────────────────────────────────────
const WidgetGalleryDialog = ({
    open,
    onClose,
    onSelectWidget,
    onCopyWidget,
    onMoveWidget,
    onOpenWidgetConfig,
    catalog = [],
    myWidgets = [],
    savedWidgets = [],
    currentDashboardId = null,
}) => {
    const dispatch = useDispatch();
    const license = useSelector(selectLicense);
    const planDefinitions = useSelector(selectPlanDefinitions);

    const [searchQuery, setSearchQuery]         = useState('');
    const [selectedWidget, setSelectedWidget]   = useState(null);
    const [selectedCategory, setSelectedCategory] = useState('הכל');
    const [activeTab, setActiveTab]             = useState('catalog');
    const [selectedTemplateIndex, setSelectedTemplateIndex] = useState({});

    useLayoutEffect(() => {
        if (open) {
            setSearchQuery('');
            setSelectedWidget(null);
            setSelectedCategory('הכל');
            setActiveTab('catalog');
            setSelectedTemplateIndex({});
        }
    }, [open]);

    // ── Filtered lists ───────────────────────────────────────────
    const catalogWidgets = catalog.filter(w => !HIDDEN_FROM_CATALOG.includes(w.componentKey));

    const filteredCatalog = catalogWidgets.filter(w => {
        const meta = getWidgetMeta(w.componentKey);
        const name = meta?.title ?? w.name ?? '';
        const matchSearch = name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            w.componentKey.toLowerCase().includes(searchQuery.toLowerCase());
        const matchCat = selectedCategory === 'הכל' || getWidgetCategory(w.componentKey) === selectedCategory;
        return matchSearch && matchCat;
    });

    const filteredMyWidgets = myWidgets.filter(w =>
        (w.componentKey ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (w.dashboardName ?? '').toLowerCase().includes(searchQuery.toLowerCase())
    );

    const filteredSaved = savedWidgets.filter(w => {
        const cw = catalog.find(c => c.componentKey === w.componentKey);
        return (cw?.name ?? w.componentKey ?? '').toLowerCase().includes(searchQuery.toLowerCase());
    });

    const currentList = activeTab === 'catalog' ? filteredCatalog
        : activeTab === 'my-widgets' ? filteredMyWidgets
        : filteredSaved;

    // ── Confirm ──────────────────────────────────────────────────
    const handleConfirm = () => {
        if (!selectedWidget) return;
        if (activeTab === 'saved') {
            onMoveWidget(selectedWidget);
        } else if (activeTab === 'my-widgets') {
            onCopyWidget(selectedWidget);
        } else {
            const tIdx = selectedTemplateIndex[selectedWidget.appId] ?? 0;
            const templateId = selectedWidget.templates?.[tIdx]?.id;
            const widgetsNeedingConfig = [
                'ZmanimWidget', 'TextWidget', 'ShabbatWidget', 'MoadWidget',
                'TefilotWidget', 'NoticeBoardWidget', 'EmergencyWidget',
                'ContactsWidget', 'SimchaWidget', 'NitzavSpotlightWidget',
                'ExpensesAnalyticsWidget', 'FinancialGaugeWidget',
                'ImageWidget', 'ParnasWidget', 'OmerWidget', 'InyaneiHaYomWidget', 'LimudimWidget', 'HalachicTimesWidget',
            ];
            onSelectWidget(
                selectedWidget.componentKey,
                templateId,
                selectedWidget.dashboardLayout,
                widgetsNeedingConfig.includes(selectedWidget.componentKey)
            );
        }
        onClose();
    };

    const switchTab = (tab) => {
        setActiveTab(tab);
        setSelectedWidget(null);
        setSearchQuery('');
        setSelectedCategory('הכל');
    };

    // License enforcement — only applies to catalog tab
    const checkWidgetLocked = (widget) => {
        if (activeTab !== 'catalog') return false;
        if (!license || !planDefinitions) return false;
        return !isWidgetAllowed(planDefinitions, license, widget);
    };

    const isSelectedLocked = selectedWidget && activeTab === 'catalog'
        ? checkWidgetLocked(selectedWidget)
        : false;

    // Derived for selected widget
    const tIdx          = selectedWidget ? (selectedTemplateIndex[selectedWidget.appId] ?? 0) : 0;
    const templateName  = selectedWidget?.templates?.[tIdx]?.name ?? 'Fixed';
    const widgetMeta    = selectedWidget ? getWidgetMeta(selectedWidget.componentKey) : null;
    const widgetCat     = selectedWidget ? getWidgetCategory(selectedWidget.componentKey) : null;
    const wizardConfig  = selectedWidget ? WIZARD_CONFIG[selectedWidget.componentKey] : null;

    const TABS = [
        { key: 'catalog',    label: 'קטלוג',       count: catalogWidgets.length },
        { key: 'my-widgets', label: 'הרכיבים שלי',  count: myWidgets.length },
        { key: 'saved',      label: 'שמורים',      count: savedWidgets.length },
    ];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '20px',
                    bgcolor: heritageColors.creamLight,
                    height: '88vh',
                    direction: 'rtl',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                },
            }}
        >
            {/* ═══ HEADER ═════════════════════════════════════════ */}
            <Box sx={{
                bgcolor: heritageColors.navy,
                px: 3,
                py: 2.5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexShrink: 0,
            }}>
                <Box>
                    <Typography sx={{ color: heritageColors.white, fontWeight: 700, fontSize: '1.1rem' }}>
                        הוספת רכיב ללוח
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.65)', mt: 0.25, fontSize: '0.78rem' }}>
                        בחר רכיב מהקטלוג, מהרכיבים הקיימים שלך, או מרכיבים שמורים
                    </Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ color: heritageColors.white, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* ═══ SPLIT SCREEN ═══════════════════════════════════ */}
            <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

                {/* ── LIST PANEL (right side in RTL) ────────────── */}
                <Box sx={{
                    width: 350,
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: heritageColors.white,
                    borderLeft: `1px solid ${heritageColors.border}`,
                    overflow: 'hidden',
                }}>
                    {/* Tabs */}
                    <Box sx={{ display: 'flex', borderBottom: `1px solid ${heritageColors.border}`, flexShrink: 0 }}>
                        {TABS.map(tab => (
                            <Box
                                key={tab.key}
                                onClick={() => switchTab(tab.key)}
                                sx={{
                                    flex: 1,
                                    py: 1.25,
                                    px: 0.5,
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    borderBottom: `2.5px solid ${activeTab === tab.key ? heritageColors.navy : 'transparent'}`,
                                    transition: 'all 0.18s',
                                    '&:hover': { bgcolor: heritageColors.creamLight },
                                }}
                            >
                                <Typography sx={{
                                    fontWeight: activeTab === tab.key ? 700 : 500,
                                    color: activeTab === tab.key ? heritageColors.navy : heritageColors.slate,
                                    fontSize: '0.78rem',
                                }}>
                                    {tab.label}
                                    {tab.count > 0 && (
                                        <Box component="span" sx={{ mr: 0.4, fontSize: '0.7rem', opacity: 0.65 }}>
                                            {` (${tab.count})`}
                                        </Box>
                                    )}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    {/* Search */}
                    <Box sx={{ px: 2, pt: 2, pb: 1.25, flexShrink: 0 }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="חיפוש..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: heritageColors.slate, fontSize: '1rem' }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '10px',
                                    bgcolor: heritageColors.creamLight,
                                    fontSize: '0.85rem',
                                    '&:hover fieldset': { borderColor: heritageColors.navy },
                                    '&.Mui-focused fieldset': { borderColor: heritageColors.navy },
                                },
                            }}
                        />
                    </Box>

                    {/* Category chips — catalog only */}
                    {activeTab === 'catalog' && (
                        <Box sx={{
                            px: 2,
                            pb: 1.5,
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 0.6,
                            flexShrink: 0,
                        }}>
                            {['הכל', ...Object.keys(WIDGET_CATEGORIES)].map(cat => (
                                <Chip
                                    key={cat}
                                    label={cat}
                                    size="small"
                                    onClick={() => setSelectedCategory(cat)}
                                    sx={{
                                        height: 24,
                                        fontSize: '0.72rem',
                                        fontWeight: selectedCategory === cat ? 700 : 500,
                                        bgcolor: selectedCategory === cat ? heritageColors.navy : 'transparent',
                                        color: selectedCategory === cat ? heritageColors.white : heritageColors.navy,
                                        border: `1.5px solid ${selectedCategory === cat ? heritageColors.navy : heritageColors.border}`,
                                        cursor: 'pointer',
                                        '&:hover': {
                                            bgcolor: selectedCategory === cat ? heritageColors.navy : heritageColors.creamLight,
                                        },
                                        '& .MuiChip-label': { px: 1 },
                                    }}
                                />
                            ))}
                        </Box>
                    )}

                    <Divider sx={{ flexShrink: 0 }} />

                    {/* Widget list */}
                    <Box key={activeTab} sx={{ flex: 1, overflowY: 'auto', py: 0.5 }}>
                        {currentList.length === 0 ? (
                            <Box sx={{ p: 4, textAlign: 'center', color: heritageColors.slate }}>
                                <Typography sx={{ fontSize: '0.85rem', opacity: 0.65 }}>
                                    {activeTab === 'catalog' ? 'לא נמצאו רכיבים' :
                                        activeTab === 'my-widgets' ? 'אין רכיבים קיימים' : 'אין רכיבים שמורים'}
                                </Typography>
                            </Box>
                        ) : currentList.map((widget) => {
                            const meta      = getWidgetMeta(widget.componentKey);
                            const name      = meta?.title ?? widget.name ?? widget.componentKey;
                            const emoji     = meta?.emoji ?? '📦';
                            const subtitle  = activeTab === 'catalog'
                                ? (meta?.description ?? '').substring(0, 58) + ((meta?.description?.length ?? 0) > 58 ? '...' : '')
                                : activeTab === 'my-widgets'
                                    ? `מלוח: ${widget.dashboardName || 'לא ידוע'}`
                                    : `שמור מ: ${widget.sourceDashboardName || 'לא ידוע'}`;
                            const isSelected = activeTab === 'catalog'
                                ? selectedWidget?.appId === widget.appId
                                : selectedWidget?.widgetId === widget.widgetId;
                            const isLocked = checkWidgetLocked(widget);

                            const handleWidgetClick = () => {
                                if (isLocked) {
                                    dispatch({ type: 'OPEN_UPGRADE_DRAWER', payload: { featureName: name, requiredPlan: '' } });
                                    return;
                                }
                                setSelectedWidget(widget);
                            };

                            return (
                                <Box
                                    key={widget.appId ?? widget.widgetId}
                                    onClick={handleWidgetClick}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 1.5,
                                        px: 2,
                                        py: 1.5,
                                        cursor: 'pointer',
                                        borderRight: `3px solid ${isSelected ? heritageColors.navy : 'transparent'}`,
                                        bgcolor: isLocked
                                            ? 'rgba(0,0,0,0.025)'
                                            : isSelected ? 'rgba(30,58,95,0.06)' : 'transparent',
                                        opacity: isLocked ? 0.65 : 1,
                                        transition: 'all 0.16s ease',
                                        '&:hover': {
                                            bgcolor: isLocked
                                                ? 'rgba(0,0,0,0.04)'
                                                : isSelected ? 'rgba(30,58,95,0.06)' : heritageColors.creamLight,
                                            borderRightColor: isSelected ? heritageColors.navy : heritageColors.border,
                                        },
                                    }}
                                >
                                    <Box sx={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: '10px',
                                        bgcolor: isLocked ? '#f1f5f9' : isSelected ? heritageColors.navy : heritageColors.creamLight,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: isLocked ? '1rem' : '1.15rem',
                                        flexShrink: 0,
                                        transition: 'background-color 0.16s ease',
                                    }}>
                                        {isLocked ? <LockIcon sx={{ fontSize: '1rem', color: '#94a3b8' }} /> : emoji}
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.2 }}>
                                            <Typography sx={{
                                                fontWeight: 600,
                                                color: isLocked ? heritageColors.slate : heritageColors.navy,
                                                fontSize: '0.85rem',
                                                lineHeight: 1.3,
                                            }}>
                                                {name}
                                            </Typography>
                                            {isLocked && (
                                                <Chip
                                                    label="חסום"
                                                    size="small"
                                                    sx={{
                                                        height: 16,
                                                        fontSize: '0.62rem',
                                                        fontWeight: 700,
                                                        bgcolor: '#f8fafc',
                                                        color: '#64748b',
                                                        border: '1px solid #e2e8f0',
                                                        '& .MuiChip-label': { px: 0.75 },
                                                    }}
                                                />
                                            )}
                                        </Box>
                                        <Typography sx={{
                                            color: heritageColors.slate,
                                            fontSize: '0.72rem',
                                            lineHeight: 1.4,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            display: 'block',
                                        }}>
                                            {isLocked ? 'רכיב חסום עבור הארגון שלך' : subtitle}
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>

                {/* ── PREVIEW PANEL (left side in RTL) ──────────── */}
                <Box sx={{
                    flex: 1,
                    display: 'flex',
                    overflow: 'hidden',
                    bgcolor: heritageColors.creamLight,
                }}>
                    {!selectedWidget ? (
                        /* Empty state */
                        <Box sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1.5,
                            p: 4,
                        }}>
                            <Box sx={{ fontSize: '3rem', opacity: 0.3, lineHeight: 1 }}>🧩</Box>
                            <Typography sx={{ fontWeight: 600, color: heritageColors.slate, opacity: 0.55, fontSize: '1rem' }}>
                                בחר רכיב מהרשימה
                            </Typography>
                            <Typography sx={{
                                color: heritageColors.slate,
                                opacity: 0.45,
                                fontSize: '0.82rem',
                                textAlign: 'center',
                                maxWidth: 300,
                                lineHeight: 1.6,
                            }}>
                                לחץ על רכיב כדי לראות תצוגה מקדימה, תיאור מפורט ואפשרויות הגדרה
                            </Typography>
                        </Box>
                    ) : (
                        /* 3-column row: [thumbnails?] [preview] [info] — all full height, no scrolling */
                        <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

                            {/* Col 1 — Thumbnail strip (right in RTL, catalog + multiple templates only) */}
                            {activeTab === 'catalog' && (selectedWidget.templates?.length ?? 0) > 1 && (
                                <Box sx={{
                                    width: 86,
                                    flexShrink: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 1,
                                    py: 1.5,
                                    px: 0.75,
                                    bgcolor: heritageColors.white,
                                    borderLeft: `1px solid ${heritageColors.border}`,
                                    overflowY: 'auto',
                                    '&::-webkit-scrollbar': { width: '3px' },
                                    '&::-webkit-scrollbar-thumb': { bgcolor: heritageColors.border, borderRadius: '4px' },
                                }}>
                                    {selectedWidget.templates.map((tmpl, ti) => {
                                        const isActive = tIdx === ti;
                                        const meta = getTemplateMeta(selectedWidget.componentKey, tmpl.name);
                                        const tooltipTitle = meta ? (
                                            <Box sx={{ maxWidth: 240, p: 0.25 }}>
                                                <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', mb: 0.5, color: 'inherit' }}>
                                                    {meta.label}
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.72rem', lineHeight: 1.55, whiteSpace: 'pre-wrap', color: 'inherit' }}>
                                                    {meta.desc}
                                                </Typography>
                                                {meta.bestFor && (
                                                    <Typography sx={{ fontSize: '0.68rem', opacity: 0.75, mt: 0.75, fontStyle: 'italic', color: 'inherit' }}>
                                                        מתאים ל: {meta.bestFor}
                                                    </Typography>
                                                )}
                                            </Box>
                                        ) : tmpl.name;

                                        return (
                                            <Tooltip key={tmpl.id} title={tooltipTitle} placement="left" arrow enterDelay={300}>
                                                <Box
                                                    onClick={() => setSelectedTemplateIndex(prev => ({ ...prev, [selectedWidget.appId]: ti }))}
                                                    sx={{
                                                        width: 64,
                                                        height: 50,
                                                        borderRadius: '8px',
                                                        border: `2px solid ${isActive ? heritageColors.navy : heritageColors.border}`,
                                                        overflow: 'hidden',
                                                        cursor: 'pointer',
                                                        flexShrink: 0,
                                                        bgcolor: heritageColors.bgLight,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        transition: 'border-color 0.15s ease',
                                                        '&:hover': { borderColor: heritageColors.navy },
                                                    }}
                                                >
                                                    <TemplatePreview
                                                        componentKey={selectedWidget.componentKey}
                                                        templateName={tmpl.name}
                                                        scale={1}
                                                    />
                                                </Box>
                                            </Tooltip>
                                        );
                                    })}
                                </Box>
                            )}

                            {/* Col 2 — Main preview */}
                            <Box sx={{
                                flex: 1,
                                bgcolor: heritageColors.bgLight,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                            }}>
                                {activeTab === 'catalog' ? (
                                    <TemplatePreview
                                        componentKey={selectedWidget.componentKey}
                                        templateName={templateName}
                                        scale={1}
                                    />
                                ) : (
                                    <RealWidgetPreview
                                        widget={selectedWidget}
                                        catalog={catalog}
                                        scale={1}
                                    />
                                )}
                            </Box>

                            {/* Col 3 — Info panel (left in RTL) */}
                            <Box sx={{
                                width: 300,
                                flexShrink: 0,
                                borderRight: `1px solid ${heritageColors.border}`,
                                bgcolor: heritageColors.white,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                                p: 2.5,
                                overflowY: 'auto',
                                '&::-webkit-scrollbar': { width: '4px' },
                                '&::-webkit-scrollbar-thumb': { bgcolor: heritageColors.border, borderRadius: '4px' },
                            }}>
                                {activeTab === 'catalog' && widgetMeta ? (
                                    <>
                                        {/* Title */}
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                            <Typography sx={{ fontSize: '1.4rem', lineHeight: 1, flexShrink: 0 }}>
                                                {widgetMeta.emoji}
                                            </Typography>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography sx={{ fontWeight: 700, color: heritageColors.navy, fontSize: '1rem', lineHeight: 1.3 }}>
                                                    {widgetMeta.title}
                                                </Typography>
                                                {widgetCat && (
                                                    <Chip
                                                        label={widgetCat}
                                                        size="small"
                                                        sx={{
                                                            mt: 0.5,
                                                            height: 20,
                                                            fontSize: '0.68rem',
                                                            fontWeight: 600,
                                                            bgcolor: heritageColors.creamLight,
                                                            color: heritageColors.navy,
                                                            border: `1px solid ${heritageColors.border}`,
                                                            '& .MuiChip-label': { px: 0.75 },
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        </Box>

                                        {/* Description */}
                                        <Typography sx={{ color: heritageColors.slate, lineHeight: 1.7, fontSize: '0.8rem' }}>
                                            {widgetMeta.description}
                                        </Typography>

                                        {/* Note callout */}
                                        {widgetMeta.note && (
                                            <Box sx={{
                                                borderRight: '3px solid #D97706',
                                                bgcolor: 'rgba(251,191,36,0.09)',
                                                borderRadius: '0 8px 8px 0',
                                                p: 1.25,
                                                pr: 1.5,
                                            }}>
                                                <Typography sx={{ fontWeight: 700, color: '#92400E', fontSize: '0.68rem', display: 'block', mb: 0.4 }}>
                                                    שים לב
                                                </Typography>
                                                <Typography sx={{ color: '#78350F', fontSize: '0.76rem', lineHeight: 1.6 }}>
                                                    {widgetMeta.note}
                                                </Typography>
                                            </Box>
                                        )}

                                        {/* WhenToUse callout */}
                                        {widgetMeta.whenToUse && (
                                            <Box sx={{
                                                borderRight: `3px solid ${heritageColors.navy}`,
                                                bgcolor: 'rgba(30,58,95,0.05)',
                                                borderRadius: '0 8px 8px 0',
                                                p: 1.25,
                                                pr: 1.5,
                                            }}>
                                                <Typography sx={{ fontWeight: 700, color: heritageColors.navy, fontSize: '0.68rem', display: 'block', mb: 0.4 }}>
                                                    מתי להשתמש?
                                                </Typography>
                                                <Typography sx={{ color: heritageColors.navy, fontSize: '0.76rem', lineHeight: 1.6, opacity: 0.8 }}>
                                                    {widgetMeta.whenToUse}
                                                </Typography>
                                            </Box>
                                        )}

                                        {/* Setup steps chips */}
                                        {wizardConfig?.steps && wizardConfig.steps.filter(s => s.id !== 'intro' && s.id !== 'template').length > 0 && (
                                            <Box>
                                                <Typography sx={{ color: heritageColors.slate, fontSize: '0.68rem', fontWeight: 600, display: 'block', mb: 0.6 }}>
                                                    שלבי הגדרה:
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                                                    {wizardConfig.steps
                                                        .filter(s => s.id !== 'intro' && s.id !== 'template')
                                                        .map(step => (
                                                            <Chip
                                                                key={step.id}
                                                                label={step.label}
                                                                size="small"
                                                                sx={{
                                                                    height: 20,
                                                                    fontSize: '0.67rem',
                                                                    bgcolor: heritageColors.creamLight,
                                                                    color: heritageColors.slate,
                                                                    border: `1px solid ${heritageColors.border}`,
                                                                    '& .MuiChip-label': { px: 0.75 },
                                                                }}
                                                            />
                                                        ))}
                                                </Box>
                                            </Box>
                                        )}
                                    </>
                                ) : (
                                    /* My Widgets / Saved */
                                    <>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                            <Typography sx={{ fontSize: '1.4rem', lineHeight: 1 }}>
                                                {getWidgetMeta(selectedWidget.componentKey)?.emoji ?? '📦'}
                                            </Typography>
                                            <Box>
                                                <Typography sx={{ fontWeight: 700, color: heritageColors.navy, fontSize: '0.95rem' }}>
                                                    {getWidgetMeta(selectedWidget.componentKey)?.title
                                                        ?? catalog.find(c => c.componentKey === selectedWidget.componentKey)?.name
                                                        ?? selectedWidget.componentKey}
                                                </Typography>
                                                <Typography sx={{ color: heritageColors.slate, fontSize: '0.72rem', mt: 0.2 }}>
                                                    {activeTab === 'my-widgets'
                                                        ? `מלוח: ${selectedWidget.dashboardName || 'לא ידוע'}`
                                                        : `שמור מ: ${selectedWidget.sourceDashboardName || 'לא ידוע'} • ${new Date((selectedWidget.savedAt?.seconds ?? 0) * 1000 || Date.now()).toLocaleDateString('he-IL')}`
                                                    }
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Box sx={{
                                            borderRight: '3px solid #059669',
                                            bgcolor: 'rgba(16,185,129,0.08)',
                                            borderRadius: '0 10px 10px 0',
                                            p: 1.5,
                                            pr: 2,
                                        }}>
                                            <Typography sx={{ fontWeight: 700, color: '#065F46', fontSize: '0.72rem', display: 'block', mb: 0.5 }}>
                                                ✨ {activeTab === 'my-widgets' ? 'רכיב מוכן להוספה' : 'רכיב שמור'}
                                            </Typography>
                                            <Typography sx={{ color: '#064E3B', fontSize: '0.78rem', lineHeight: 1.65 }}>
                                                {activeTab === 'my-widgets'
                                                    ? `רכיב זה לקוח מלוח "${selectedWidget.dashboardName || 'לא ידוע'}" — כולל את כל ההגדרות שהוגדרו שם. הוספה מיידית ללא צורך בהגדרה מחדש.`
                                                    : 'רכיב שמור עם כל הפרמטרים המוגדרים — חוסך הגדרה מלאה מחדש ומאפשר שימוש חוזר מהיר בתצורה מוכחת.'
                                                }
                                            </Typography>
                                        </Box>
                                    </>
                                )}
                            </Box>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* ═══ FOOTER ═════════════════════════════════════════ */}
            <Box sx={{
                flexShrink: 0,
                bgcolor: heritageColors.white,
                borderTop: `1px solid ${heritageColors.border}`,
                px: 3,
                py: 2,
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: 1.5,
            }}>
                <Button
                    onClick={onClose}
                    sx={{
                        color: heritageColors.slate,
                        fontWeight: 500,
                        textTransform: 'none',
                        fontSize: '0.875rem',
                    }}
                >
                    ביטול
                </Button>
                <Button
                    variant="contained"
                    onClick={handleConfirm}
                    disabled={!selectedWidget || isSelectedLocked}
                    startIcon={<AddIcon />}
                    sx={{
                        bgcolor: heritageColors.navy,
                        color: heritageColors.white,
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: '10px',
                        px: 3,
                        fontSize: '0.875rem',
                        '&:hover': { bgcolor: heritageColors.navyDark || '#152844' },
                        '&.Mui-disabled': { bgcolor: heritageColors.border, color: heritageColors.slate },
                    }}
                >
                    {activeTab === 'catalog'    ? 'הוסף רכיב'   :
                     activeTab === 'my-widgets' ? 'העתק ללוח'   :
                                                  'העבר ללוח'}
                </Button>
            </Box>
        </Dialog>
    );
};

export default WidgetGalleryDialog;
